import {
  toUtcTimestamp,
  toZonedDisplayDate,
  wallClockToDisplayDate,
} from "./timezone.js";

export const START_MODE_NOW = "now";
export const START_MODE_SCHEDULED = "scheduled";
export const END_MODE_ALWAYS_ON = "alwaysOn";
export const END_MODE_SCHEDULED = "scheduled";
export const DEFAULT_VALIDITY_START_TIME = "00:00";
export const DEFAULT_VALIDITY_END_TIME = "23:59";

const START_NOW_TOLERANCE_MS = 2 * 60 * 1000;

const pad = (value) => String(value).padStart(2, "0");

export const wallDateFromValue = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return "";
  }
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(
    value.getDate()
  )}`;
};

export const wallTimeFromValue = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return "";
  }
  return `${pad(value.getHours())}:${pad(value.getMinutes())}`;
};

export const timestampFromFormWall = ({ dateValue, timeValue, timezone }) => {
  const datePart =
    dateValue instanceof Date ? wallDateFromValue(dateValue) : dateValue;
  const wall = wallClockToDisplayDate({
    date: datePart,
    time: timeValue,
  });
  if (!wall) return null;
  return toUtcTimestamp({ date: wall, timezone });
};

export const zonedPartsFromTimestamp = ({ timestamp, timezone }) => {
  const zoned = toZonedDisplayDate({ date: timestamp, timezone });
  if (!zoned) return null;
  return {
    date: zoned,
    time: wallTimeFromValue(zoned),
  };
};

export const applyWallTimeToDate = (dateValue, timeValue) => {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
    return dateValue;
  }
  if (typeof timeValue !== "string" || !timeValue) {
    return dateValue;
  }
  const [hours, minutes] = timeValue.split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return dateValue;
  }
  const next = new Date(dateValue);
  next.setHours(hours, minutes, 0, 0);
  return next;
};

export const clampEndAfterStart = ({
  startDate,
  startTime,
  endDate,
  endTime,
}) => {
  const start = applyWallTimeToDate(startDate, startTime);
  const end = applyWallTimeToDate(endDate, endTime);
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) {
    return {
      validityEndDate: endDate,
      validityEndTime: endTime,
    };
  }
  if (end instanceof Date && !Number.isNaN(end.getTime()) && end > start) {
    return {
      validityEndDate: endDate,
      validityEndTime: endTime,
    };
  }
  const next = applyWallTimeToDate(new Date(start.getTime()), "23:59");
  if (next <= start) {
    next.setDate(next.getDate() + 1);
  }
  return {
    validityEndDate: next,
    validityEndTime: wallTimeFromValue(next),
  };
};

export const buildDefaultValidityFields = (now = new Date()) => {
  const start = applyWallTimeToDate(now, DEFAULT_VALIDITY_START_TIME);
  start.setDate(start.getDate() + 1);
  const end = applyWallTimeToDate(
    new Date(now.getTime()),
    DEFAULT_VALIDITY_END_TIME
  );
  end.setDate(end.getDate() + 7);
  return {
    startMode: START_MODE_NOW,
    validityStartDate: start,
    endMode: END_MODE_ALWAYS_ON,
    validityEndDate: end,
  };
};

export const resolvePersistedActiveFrom = ({ schedule, createdAt }) => {
  const stored = Number(schedule?.activeFrom);
  if (Number.isFinite(stored) && stored > 0) return stored;
  const created = Number(createdAt);
  if (Number.isFinite(created) && created > 0) return created;
  return null;
};

export const isAutomationStartLocked = ({
  schedule,
  createdAt,
  now = Date.now(),
}) => {
  const activeFrom = resolvePersistedActiveFrom({ schedule, createdAt });
  if (!Number.isFinite(activeFrom)) return true;
  return activeFrom <= now;
};

export const inferStartMode = ({ schedule, createdAt }) => {
  const activeFrom = Number(schedule?.activeFrom);
  if (!Number.isFinite(activeFrom) || activeFrom <= 0) {
    return START_MODE_NOW;
  }
  const created = Number(createdAt);
  if (
    Number.isFinite(created) &&
    Math.abs(activeFrom - created) < 3 * 60 * 1000
  ) {
    return START_MODE_NOW;
  }
  return START_MODE_SCHEDULED;
};

export const resolveFormActiveFrom = ({ formState, now = Date.now() }) => {
  if (formState.startMode === START_MODE_NOW) {
    return now;
  }
  const timestamp = timestampFromFormWall({
    dateValue: formState.validityStartDate,
    timeValue: DEFAULT_VALIDITY_START_TIME,
    timezone: formState.timezone,
  });
  if (Number.isFinite(timestamp) && timestamp < now) {
    const endOfDay = timestampFromFormWall({
      dateValue: formState.validityStartDate,
      timeValue: DEFAULT_VALIDITY_END_TIME,
      timezone: formState.timezone,
    });
    if (Number.isFinite(endOfDay) && endOfDay >= now) {
      return now;
    }
  }
  return timestamp;
};

export const resolveFormActiveUntil = ({ formState }) => {
  if (formState.endMode !== END_MODE_SCHEDULED) {
    return null;
  }
  return timestampFromFormWall({
    dateValue: formState.validityEndDate,
    timeValue: DEFAULT_VALIDITY_END_TIME,
    timezone: formState.timezone,
  });
};

export const getScheduleValidityMessage = ({
  formState,
  t,
  keys,
  isStartLocked = false,
  now = Date.now(),
}) => {
  if (!isStartLocked && formState.startMode === START_MODE_SCHEDULED) {
    const activeFrom = resolveFormActiveFrom({ formState, now });
    if (!Number.isFinite(activeFrom)) {
      return t(keys.startRequired);
    }
    if (activeFrom < now - START_NOW_TOLERANCE_MS) {
      return t(keys.startFuture);
    }
  }

  if (formState.endMode === END_MODE_SCHEDULED) {
    const activeUntil = resolveFormActiveUntil({ formState });
    if (!Number.isFinite(activeUntil)) {
      return t(keys.endRequired);
    }
    if (activeUntil < now - START_NOW_TOLERANCE_MS) {
      return t(keys.endFuture);
    }
    const activeFrom = isStartLocked
      ? Number(formState.lockedActiveFrom) ||
        resolveFormActiveFrom({ formState, now })
      : resolveFormActiveFrom({ formState, now });
    if (Number.isFinite(activeFrom) && activeUntil <= activeFrom) {
      return t(keys.endAfterStart);
    }
  }

  return null;
};
